"use client";

import { memo, type ReactNode, useState } from "react";
import { IconSelector } from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type CustomSelectOption<T extends string = string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

type CustomSelectStyles = {
  trigger?: string;
  content?: string;
  item?: string;
};

type CustomSelectProps<
  T extends string = string,
  TOption extends CustomSelectOption<T> = CustomSelectOption<T>,
> = {
  options: readonly TOption[];
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  placeholder?: string;
  title?: string;
  ariaLabel?: string;
  showIcon?: boolean;
  className?: string;
  styles?: CustomSelectStyles;
  renderTriggerValue?: (option: TOption | undefined) => ReactNode;
  renderItem?: (option: TOption, isSelected: boolean) => ReactNode;
  inlineExpanded?: boolean;
  onInlineExpandedChange?: (open: boolean) => void;
  disabled?: boolean;
};

function CustomSelectInner<
  T extends string = string,
  TOption extends CustomSelectOption<T> = CustomSelectOption<T>,
>({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Select an option",
  title,
  ariaLabel,
  showIcon = true,
  className,
  styles,
  renderTriggerValue,
  renderItem,
  inlineExpanded,
  onInlineExpandedChange,
  disabled = false,
}: CustomSelectProps<T, TOption>) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isInline = typeof onInlineExpandedChange === "function";
  const isOpen = isInline ? inlineExpanded : internalOpen;

  const handleOpenChange = (nextOpen: boolean) => {
    if (isInline) {
      onInlineExpandedChange?.(nextOpen);
    } else {
      setInternalOpen(nextOpen);
    }
  };

  const selectedOption = options.find((option) => option.value === value);
  const renderedValue = renderTriggerValue?.(selectedOption);

  if (isInline) {
    return (
      <div className={cn("space-y-3", className)}>
        <button
          type="button"
          onClick={() => handleOpenChange(!isOpen)}
          aria-label={ariaLabel ?? placeholder}
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between gap-3 rounded-full border border-[#E7E7E7] bg-white px-4 text-left text-[16px] font-medium text-[#2D2D2D] shadow-none transition-colors focus:border-[#3BDAFF] focus:outline-none",
            !selectedOption && "text-[#B5B7BA]",
            disabled && "cursor-not-allowed opacity-50",
            styles?.trigger,
          )}
        >
          <div className="min-w-0 flex-1">
            {renderedValue ??
              (selectedOption ? (
                <span className="truncate text-[#2D2D2D]">
                  {selectedOption.label}
                </span>
              ) : (
                <span className="truncate text-[#B5B7BA]">{placeholder}</span>
              ))}
          </div>
          {showIcon ? (
            <IconSelector
              stroke={2}
              className="size-6 shrink-0 text-[#B5B7BA]"
            />
          ) : null}
        </button>

        <AnimatePresence initial={false}>
          {isOpen ? (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
              className={cn(
                "rounded-3xl border border-[#EDEEEE] bg-white p-0 shadow-sm",
                styles?.content,
              )}
            >
              <div className="py-2">
                {title ? (
                  <div className="px-4 pb-2 pt-3">
                    <p className="text-left text-[16px] font-semibold leading-6 text-[#2D2D2D]">
                      {title}
                    </p>
                  </div>
                ) : null}

                {options.map((option) => {
                  const isSelected = option.value === value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={option.disabled}
                      onClick={() => {
                        onValueChange?.(option.value as T);
                        handleOpenChange(false);
                      }}
                      className={cn(
                        "flex w-full items-center mt-2 rounded-xl text-left text-[16px] font-semibold leading-6 transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                        isSelected
                          ? "bg-[#F8F9FA] text-[#2D2D2D]"
                          : "text-[#2D2D2D] hover:bg-[#F8F9FA]",
                        styles?.item,
                      )}
                    >
                      {renderItem ? (
                        renderItem(option, isSelected)
                      ) : (
                        <span className="truncate">{option.label}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className={className}>
      <Select
        value={value}
        defaultValue={defaultValue}
        onValueChange={(nextValue) => onValueChange?.(nextValue as T)}
        open={internalOpen}
        onOpenChange={setInternalOpen}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={ariaLabel ?? placeholder}
          className={cn(
            "h-12 rounded-full border border-[#E7E7E7] bg-white px-4 text-left text-[16px] font-medium text-[#2D2D2D] shadow-none transition-colors focus:border-[#3BDAFF] focus:ring-0 data-[placeholder]:text-[#B5B7BA] [&>svg]:hidden",
            disabled && "cursor-not-allowed opacity-50",
            styles?.trigger,
          )}
        >
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              {renderedValue ??
                (selectedOption ? (
                  <span className="truncate">{selectedOption.label}</span>
                ) : (
                  <span className="truncate text-[#B5B7BA]">{placeholder}</span>
                ))}
            </div>
            {showIcon ? (
              <IconSelector
                stroke={2}
                className="size-6 shrink-0 text-[#B5B7BA]"
              />
            ) : null}
          </div>
        </SelectTrigger>

        <SelectContent
          position="popper"
          className={cn(
            "min-w-[var(--radix-select-trigger-width)] max-h-[400px] overflow-y-auto rounded-3xl border border-[#EDEEEE] bg-white p-0 shadow-md",
            styles?.content,
          )}
        >
          <div className="py-2">
            {title ? (
              <div className="px-4 pb-2 pt-3">
                <p className="text-left text-[16px] font-semibold leading-6 text-[#2D2D2D]">
                  {title}
                </p>
              </div>
            ) : null}

            {options.map((option) => {
              const isSelected = option.value === value;

              return (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={cn(
                    "mt-2 h-full rounded-xl text-left text-[16px] font-semibold leading-6 text-[#2D2D2D] hover:cursor-pointer hover:bg-[#F8F9FA] focus:text-[#2D2D2D] data-[highlighted]:bg-[#F8F9FA] data-[highlighted]:text-[#2D2D2D] data-[state=checked]:bg-transparent data-[state=checked]:text-[#2D2D2D]",
                    styles?.item,
                  )}
                >
                  {renderItem ? (
                    renderItem(option, isSelected)
                  ) : (
                    <span className="truncate">{option.label}</span>
                  )}
                </SelectItem>
              );
            })}
          </div>
        </SelectContent>
      </Select>
    </div>
  );
}

export default memo(CustomSelectInner) as typeof CustomSelectInner;
